import {memo} from 'react'
import {Timeline, RelativeTime} from '@primer/react'
import type {IssueEventInfo, TimelineItem} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {TimelineItemBadge} from './TimelineItemBadge'
import styles from './TimelineEvent.module.css'
import panelStyles from './TimelinePanel.module.css'
import {TimelineAuthor} from './TimelineAuthor'
import {PullRequestReviewText} from './items/PullRequestReviewText'
import {PullRequestReviewAction} from './items/PullRequestReviewAction'
import {hasLinkedStackReference, IssueEventText} from './items/IssueEventText'
import {PullRequestCommitText} from './items/PullRequestCommitText'
import {OtherItemText} from './items/OtherItemText'
import {CrossReferenceText} from './items/CrossReferenceText'
import {IssueCommentAction} from './items/IssueCommentAction'
import {CopilotWorkStartedEventText} from './items/CopilotWorkStartedEventText'

// narrow on the additionalInfo oneof so each leaf receives its proto info value
export function TimelineItemText({item}: {item: TimelineItem}) {
  const info = item.additionalInfo?.info
  switch (info?.case) {
    case 'pullRequestReview':
      return info.value.commentCount && info.value.reviewId ? (
        <PullRequestReviewAction reviewId={info.value.reviewId} commentCount={info.value.commentCount}>
          <PullRequestReviewText info={info.value} />
        </PullRequestReviewAction>
      ) : (
        <PullRequestReviewText info={info.value} />
      )
    case 'issueComment':
      return <IssueCommentAction info={info.value} />
    case 'pullRequestCommit':
      return <PullRequestCommitText info={info.value} />
    case 'issueEvent':
      return <IssueEventText info={info.value} />
    case 'copilotWorkStartedEvent':
      return <CopilotWorkStartedEventText item={item} />
    case 'crossReference':
      return <CrossReferenceText info={info.value} />
    default:
      return <OtherItemText item={item} />
  }
}

export function timelineItemTextHasInteractiveContent(item: TimelineItem) {
  const info = item.additionalInfo?.info

  switch (info?.case) {
    case 'pullRequestReview':
      return Boolean(info.value.commentCount && info.value.reviewId)
    case 'issueComment':
      return Boolean(info.value.commentId)
    case 'pullRequestCommit':
    case 'crossReference':
    case 'copilotWorkStartedEvent':
      return true
    case 'issueEvent':
      return issueEventHasInteractiveContent(info.value)
    default:
      return false
  }
}

function issueEventHasInteractiveContent(info: IssueEventInfo) {
  const hasLink =
    Boolean(info.sessionUrl || info.mergeQueueUrl || info.afterCommitOid) ||
    info.eventType === 'referenced' ||
    hasLinkedStackReference(info)
  const hasExpandableChecks = Boolean(info.checksCommitOid)
  return hasLink || hasExpandableChecks
}

export const TimelineEvent = memo(function TimelineEventComponent({item}: {item: TimelineItem}) {
  return (
    <Timeline.Item className={panelStyles.activityItem}>
      <TimelineItemBadge item={item} />
      <Timeline.Body className={`${panelStyles.activityBody} d-flex flex-row flex-justify-between flex-items-start`}>
        <span className="flex-1 min-width-0 mr-2">
          <TimelineAuthor
            avatarUrl={item.user?.avatarUrl}
            login={item.user?.displayLogin}
            slug={item.user?.slug}
            userType={item.user?.userType}
          />
          <TimelineItemText item={item} />
        </span>
        <span
          className={`${panelStyles.activityActions} d-flex flex-row flex-items-center flex-shrink-0 fgColor-muted`}
        >
          <RelativeTime className={styles.timestamp} datetime={item.timestamp} format="micro" />
        </span>
      </Timeline.Body>
    </Timeline.Item>
  )
})
